"use client";

import { useQuery } from "@tanstack/react-query";
import { useLocale } from "next-intl";
import { api } from "@/shared/lib/api";
import { championPlaysLane, type ChampionLane } from "@/shared/config/champion-lanes";
import { registerChampionCatalog } from "@/shared/lib/champion-identity";
import {
  fetchGameStaticManifest,
  championImageFromManifest,
  registerManifestImages,
} from "@/shared/lib/game-static";
import { QUERY_VOLATILITY } from "@/shared/config/api-constants";

export interface ChampionDto {
  id: string;
  /** Готовый адрес иконки с CDN (KAN-906). null — берём из champion-image.ts. */
  iconUrl: string | null;
  portraitUrl: string | null;
  /** Имя в языке интерфейса — его и показываем. */
  name: string;
  /** Английское имя: по нему тоже ищут («Kaisa» с привычки из клиента). */
  nameEn: string;
  key: string;
  imageFull: string;
  tags: string[];
}

/**
 * KAN-895: бэкенд отдаёт каталог с локализованным и английским именем сразу
 * (`GET /predraft/champions?locale=…`). Раньше приходил сырой ответ Data Dragon на
 * en_US — русский интерфейс подписывал чемпионов латиницей.
 */
interface ChampionCatalogEntry {
  id: string;
  key: string;
  name: string;
  nameEn: string;
  tags?: string[] | null;
}

async function fetchChampions(locale: string): Promise<ChampionDto[]> {
  // KAN-906: сначала манифест с CDN — он лежит готовым и не требует ни Riot, ни нашего
  // бэкенда. Ручка остаётся фолбэком: сразу после выката CDN ещё пуст.
  const manifest = await fetchGameStaticManifest();
  if (manifest) {
    // KAN-907: адреса из манифеста — общий источник для карточек турниров, бордов
    // матчей и комнаты. Иначе каждый потребитель собирал бы URL по своим правилам.
    registerManifestImages(manifest.champions);
    return manifest.champions.map((c) => ({
      id: c.id,
      iconUrl: championImageFromManifest(c, "icon"),
      portraitUrl: championImageFromManifest(c, "portrait"),
      name: c.name || c.nameEn || c.id,
      nameEn: c.nameEn || c.id,
      key: c.key,
      imageFull: `${c.id}.png`,
      tags: Array.isArray(c.tags) ? c.tags : [],
    }));
  }

  const { data } = await api.get<ChampionCatalogEntry[]>("/predraft/champions", {
    params: { locale },
  });

  // Ветка разбора старого словаря Data Dragon удалена (KAN-895): она жила на окно
  // между выкатками фронта и бэкенда, и это окно закрыто — прод отдаёт каталог
  // массивом (проверено 03.09.2026). Массив на всякий случай проверяем: ручка
  // может ответить пустым телом при недоступном Data Dragon, и падать на этом
  // нельзя — комната обойдётся заглушками.
  const raw: ChampionCatalogEntry[] = Array.isArray(data) ? data : [];

  return raw.map((c) => ({
    id: c.id,
    iconUrl: null,
    portraitUrl: null,
    name: c.name || c.nameEn || c.id,
    nameEn: c.nameEn || c.id,
    key: c.key,
    imageFull: `${c.id}.png`,
    tags: Array.isArray(c.tags) ? c.tags : [],
  }));
}

/**
 * Поиск идёт по обоим именам и по id: игрок набирает и «Ясуо», и «Yasuo», и «Yasuo»
 * встречается в никах и на стримах. Потерять латиницу ради перевода нельзя.
 */
export function championMatchesQuery(champ: ChampionDto, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return (
    champ.name.toLowerCase().includes(q) ||
    champ.nameEn.toLowerCase().includes(q) ||
    champ.id.toLowerCase().includes(q)
  );
}

/**
 * Фильтр комнаты («Top», «Jungle», …) → линия из карты (KAN-896, KAN-920).
 *
 * Ключи фильтра исторически латинские и с большой буквы, ключи карты — те же, что
 * у иконок ролей. Соответствие держим одной таблицей, а не приведением регистра:
 * «ADC» и «adc» совпадают случайно, а не по правилу, и на следующем фильтре
 * («Fill», «Coach») такое приведение молча дало бы неверный результат.
 */
const FILTER_TO_LANE: Record<string, ChampionLane> = {
  Top: "top",
  Jungle: "jungle",
  Mid: "mid",
  ADC: "adc",
  Support: "support",
};

/**
 * Подходит ли чемпион под выбранную линию (KAN-896).
 *
 * Раньше здесь сопоставлялись `tags` Data Dragon — это КЛАСС персонажа, а не линия:
 * «Лес» → Fighter/Tank/Assassin затягивал Гарена с Мальфитом и терял Иву. Линии нет
 * ни в одном автоматическом источнике, поэтому она берётся из курируемой карты.
 *
 * Чемпион без записи в карте не подходит под конкретную линию, но остаётся в «Все»
 * и находится поиском: отсутствие данных не должно выглядеть как отсутствие чемпиона.
 */
export function championMatchesRole(champ: ChampionDto, role: string): boolean {
  if (role === "All") return true;
  const lane = FILTER_TO_LANE[role];
  // Неизвестный фильтр не должен молча прятать весь список.
  if (!lane) return true;
  return championPlaysLane(champ.id, lane);
}

export function useChampions() {
  const locale = useLocale();
  return useQuery({
    queryKey: ["predraft", "champions", locale],
    queryFn: async () => {
      const champions = await fetchChampions(locale);
      // KAN-921: каталог патча — источник сопоставления «имя → id» для всего фронта.
      // Регистрируем его там, где он и так загружается, чтобы картинки и статистика
      // не строили свои списки исключений.
      registerChampionCatalog(champions);
      return champions;
    },
    ...QUERY_VOLATILITY.REFERENCE,
  });
}
